import { BlankTitleInsurance } from '../chicago/chicago.component';
import { AddTitleInsuranceComponent } from '../components/add-title-insurance/add-title-insurance.component';
import { ClearOrderModalComponent } from './components/clear-order-modal/clear-order-modal.component';
import { SwitchToIntegratedModalComponent } from './components/switch-to-integrated-modal/switch-to-integrated-modal.component';
import { Component, Input, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { openClose } from '@core/animations/animations';
import { TitleInsuranceContact } from '@models/businesses';
import {
	MatterManualTitleInsurance,
	TitleInsuranceCompanyName,
	TitleInsurancePolicy,
	TitleInsurancePolicyType,
	TitleInsuranceProvider,
} from '@models/title-insurance';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot, ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { BusinessState } from '@state/business';
import { MatterDispatch } from '@state/matter';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { uniq } from 'lodash';
import { Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { TestHooks } from './manual.hooks';

@UntilDestroy()
@Component({
	selector: 'app-manual-title-insurance',
	templateUrl: './manual.component.html',
	styleUrls: ['./manual.component.scss'],
	animations: [openClose()],
	standalone: false,
})
export class ManualTitleInsuranceComponent implements OnInit {
	testHooks = TestHooks;
	public allLenderPoliciesHavePriority = true;
	public allLenderPoliciesHaveUniquePriority = true;
	public companyLogo: string;
	public company: string;
	public contact: TitleInsuranceContact;

	@Input() showHeader = true;

	@ViewSelectSnapshot(BusinessState.fctContact)
	fctContact: TitleInsuranceContact;
	@ViewSelectSnapshot(BusinessState.chicagoContact)
	chicagoContact: TitleInsuranceContact;
	@ViewSelectSnapshot(BusinessState.stewartContact)
	stewartContact: TitleInsuranceContact;
	@ViewSelectSnapshot(BusinessState.titlePlusContact)
	titlePlusContact: TitleInsuranceContact;
	@Select(SelectedMatter.titleInsurancePolicies) policies$: Observable<
		TitleInsurancePolicy[]
	>;
	@Select(SelectedMatter.getTitleInsurance)
	titleInsurance$: Observable<MatterManualTitleInsurance>;
	@Select(SelectedMatter.titleInsuranceAmountDueByClient)
	amountDueByClient$: Observable<number>;
	@SelectSnapshot(SelectedMatter.id) matterId: string;

	constructor(
		private matterDispatch: MatterDispatch,
		private dialog: MatDialog,
	) {}

	ngOnInit(): void {
		this.policies$.pipe(untilDestroyed(this)).subscribe((policies) => {
			const lenderPolicies =
				policies?.filter((x) => x.type === TitleInsurancePolicyType.LENDER) ??
				[];
			const lenderPoliciesWithPriority =
				lenderPolicies.filter((x) => !!x.priority) ?? [];
			const unqiuePriorities =
				uniq(lenderPoliciesWithPriority.map((x) => x.priority)) ?? [];

			this.allLenderPoliciesHavePriority =
				lenderPolicies.length === lenderPoliciesWithPriority.length;
			this.allLenderPoliciesHaveUniquePriority =
				unqiuePriorities.length === lenderPoliciesWithPriority.length;
		});

		this.titleInsurance$
			.pipe(untilDestroyed(this))
			.subscribe((titleInsurance) => {
				this.company = titleInsurance?.companyName;
				switch (this.company) {
					case TitleInsuranceCompanyName[TitleInsuranceProvider.FCT]:
						this.companyLogo = '/assets/images/integrations/fct-logo.svg';
						this.contact = this.fctContact;
						break;
					case TitleInsuranceCompanyName[TitleInsuranceProvider.CHICAGO]:
						this.companyLogo =
							'/assets/images/integrations/chicago-title-logo.svg';
						this.contact = this.chicagoContact;
						break;
					case TitleInsuranceCompanyName[TitleInsuranceProvider.STEWART]:
						this.companyLogo =
							'/assets/images/integrations/stewart-title-logo.svg';
						this.contact = this.stewartContact;
						break;
					case TitleInsuranceCompanyName[TitleInsuranceProvider.TITLE_PLUS]:
						this.companyLogo =
							'/assets/images/integrations/title-plus-logo.svg';
						this.contact = this.titlePlusContact;
						break;
					default:
						this.companyLogo =
							'/assets/images/integrations/title-plus-logo.svg';
						break;
				}
			});
	}

	switchToIntegrated(): void {
		this.dialog
			.open(SwitchToIntegratedModalComponent, {
				width: '550px',
				maxWidth: '90vw',
			})
			.afterClosed()
			.pipe(
				untilDestroyed(this),
				filter((result) => !!result),
			)
			.subscribe(() => this.dialog.open(AddTitleInsuranceComponent));
	}

	removeTitleInsurance(): void {
		this.matterDispatch.SetTitleInsurance(this.matterId, {
			...BlankTitleInsurance,
		});
	}

	clear(): void {
		this.dialog
			.open(ClearOrderModalComponent)
			.afterClosed()
			.pipe(
				untilDestroyed(this),
				filter((result) => !!result),
			)
			.subscribe(() =>
				this.matterDispatch.SetTitleInsurance(this.matterId, {
					...BlankTitleInsurance,
				}),
			);
	}
}
